import React, { FC, useEffect, useState } from 'react';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Card, Col, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import Moment from 'react-moment';
import useFormList from '../../hooks/api/form/useFormList';
import PaginationComponent from '../../components/pagination/PaginationComponent';
import useDeleteForm from '../../hooks/api/form/useDeleteForm';
import CloneFormModal from './components/CloneFormModal';
import CreateNewFormModal from './components/CreateNewFormModal';
import DeleteFormModal from './components/DeleteFormModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import EmptyList from '../../components/layout/emptyList/EmptyList';
import LoadingSpinner from '../../components/layout/loadingSpinner/LoadingSpinner';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../models/security/security';
import { useHistory } from 'react-router';
import { getText } from '../../utilities/CultureUtilities';
import { FallbackPage } from '../../components/layout/navbar/Navbar';

const RegistrationsPage: FC<FormListProps> = () => {
  const [openCreateFormModal, setOpenCreateformModal] = useState(false);
  const [openCloneFormModal, setOpenCloneFormModal] = useState<any>(false);
  const [openDeleteId, setOpenDeleteId] = useState<string | undefined>();
  const [formSecurity, setFormSecurity] = useState<SecurityModel | null>();
  const [formSubmissionSecurity, setFormSubmissionSecurity] = useState<SecurityModel | null>();
  const { forms, setOrganizationId, isLoading, currentPage, getFormsByPage, pageSize, totalCount } = useFormList();
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { deleteForm, isLoading: isLoadingDeleteForm, setOrganizationId: setId } = useDeleteForm(organizationId);
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const history = useHistory();

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
      setId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    let formSecurityValue = formSecurity;
    if (security) {
      formSecurityValue = getSecuritySection(security, SecuritySections.FORM);
      setFormSecurity(getSecuritySection(security, SecuritySections.FORM));
      setFormSubmissionSecurity(getSecuritySection(security, SecuritySections.FORM_SUBMISSION));
    }
    if (formSecurityValue && !formSecurityValue.read) {
      history.push(FallbackPage);
    }
  }, [security]);

  const renderHeader = () => {
    return (
      <>
        <thead>
          <tr>
            <th className="text-start form-list-header">
              <FormattedMessage id={'formList.columns.formName'}></FormattedMessage>
            </th>
            <th className="text-center form-list-header">
              <FormattedMessage id={'formList.columns.submission'}></FormattedMessage>
            </th>
            <th className="text-center form-list-header">
              <FormattedMessage id={'formList.columns.lastUpdate'}></FormattedMessage>
            </th>
            <th className="text-center form-list-header">
              <FormattedMessage id={'formList.columns.editedBy'}></FormattedMessage>
            </th>
            <th className="text-end actions">
              <FormattedMessage id={'formList.columns.actions'}></FormattedMessage>
            </th>
          </tr>
        </thead>
      </>
    );
  };

  const renderSubmission = (data) => {
    if (data.submissionLimit) {
      return (
        <span>
          {data.submissionCount}/{data.submissionLimit}
        </span>
      );
    } else {
      return <span>{data.submissionCount}</span>;
    }
  };

  const renderLastUpdate = (lastUpdate) => {
    return (
      <Moment fromNow utc>
        {lastUpdate}
      </Moment>
    );
  };

  const renderEditedBy = (editedBy) => {
    return (
      <span>
        {editedBy?.firstName} {editedBy?.name}
      </span>
    );
  };

  const renderRows = () => {
    return (
      <>
        <tbody>
          {forms?.map((form, index) => (
            <tr className="submission-row" key={index}>
              <td className="text-start text-column">{getText(form.name, intl.locale)}</td>
              <td className="text-center ">{renderSubmission(form)}</td>
              <td className="text-center ">{renderLastUpdate(form.modificationDate)}</td>
              <td className="text-center text-column">{renderEditedBy(form.editedBy)}</td>
              <td className="text-end text-nowrap">
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'formList.buttons.submissions' }))}
                  placement="top"
                >
                  <Button
                    variant="primary"
                    className="d-inline-flex align-items-center justify-content-center me-1"
                    onClick={() => history.push(`/registrations/submissions/${form.id}`)}
                    disabled={!formSubmissionSecurity?.read}
                  >
                    <span className="mdi mdi-view-list-outline"></span>
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'formList.buttons.preview' }))}
                  placement="top"
                >
                  <Button
                    variant="primary"
                    className="d-inline-flex align-items-center justify-content-center me-1"
                    onClick={() => history.push(`/registrations/preview/${form.id}`)}
                    disabled={!formSecurity?.read}
                  >
                    <span className="mdi mdi-eye-outline"></span>
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'formList.buttons.editForm' }))}
                  placement="top"
                >
                  <Button
                    variant="primary"
                    className="d-inline-flex align-items-center justify-content-center me-1"
                    onClick={() => history.push(`/registrations/admin/edit-form/${form.id}`)}
                    disabled={!formSecurity?.update || !formSecurity?.read}
                  >
                    <span className="mdi mdi-pencil-outline"></span>
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'formList.buttons.cloneForm' }))}
                  placement="top"
                >
                  <Button
                    className="d-inline-flex align-items-center justify-content-center me-1"
                    variant="primary"
                    onClick={() => {
                      setOpenCloneFormModal(form.id);
                    }}
                    disabled={!formSecurity?.write}
                  >
                    <span className="mdi mdi-content-copy"></span>
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={renderTooltip(intl.formatMessage({ id: 'formList.buttons.delete' }))}
                  placement="top"
                >
                  <Button
                    className="d-inline-flex align-items-center justify-content-center"
                    variant="primary"
                    onClick={() => {
                      setOpenDeleteId(form.id);
                    }}
                    disabled={!formSecurity?.delete}
                  >
                    <span className="mdi mdi-trash-can-outline"></span>
                  </Button>
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </>
    );
  };

  const confirmDelete = async () => {
    if (openDeleteId && (await deleteForm(openDeleteId))) {
      getFormsByPage(1);
    }
    setOpenDeleteId(undefined);
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  return (
    <>
      <DeleteFormModal
        open={openDeleteId !== undefined}
        close={() => setOpenDeleteId(undefined)}
        confirmDelete={confirmDelete}
        loading={isLoadingDeleteForm}
        formName={getText(_.find(forms, { id: openDeleteId })?.name, intl.locale)}
      />
      <CreateNewFormModal open={openCreateFormModal} close={() => setOpenCreateformModal(false)} />
      <CloneFormModal
        formName={getText(_.find(forms, { id: openCloneFormModal })?.name, intl.locale)}
        formId={openCloneFormModal}
        open={openCloneFormModal}
        close={(cloned: boolean) => {
          if (cloned) {
            getFormsByPage(currentPage);
          }
          setOpenCloneFormModal(false);
        }}
      />
      <Col xl={12} className="text-end mb-2">
        <Button
          variant="primary"
          className="me-1"
          onClick={() => setOpenCreateformModal(true)}
          disabled={!formSecurity?.write}
        >
          <span className="mdi mdi-plus pe-1"></span>
          <span>{intl.formatMessage({ id: 'formList.buttons.createForm' })}</span>
        </Button>
      </Col>
      {isLoading && <LoadingSpinner />}
      <Card>
        <Card.Body>
          {!isLoading && forms && forms.length > 0 ? (
            <Table>
              {renderHeader()}
              {renderRows()}
            </Table>
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center">
              <EmptyList message={intl.formatMessage({ id: 'formList.forms.noForms' })} />
              <Button variant="primary" className="me-1" onClick={() => setOpenCreateformModal(true)}>
                <span className="mdi mdi-plus pe-1"></span>
                <span>{intl.formatMessage({ id: 'formList.buttons.createForm' })}</span>
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
      {!isLoading && forms && forms.length > 0 && (
        <PaginationComponent
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onChange={(val) => getFormsByPage(val)}
        />
      )}
    </>
  );
};

type FormListProps = unknown;

export default RegistrationsPage;
